import { useCallback, useEffect, useState } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";

const ACTION_COLOR = {
  "auth.login": "var(--mq-green)",
  "auth.logout": "var(--mq-text-2)",
  "bot.start": "var(--mq-green)",
  "bot.stop": "var(--mq-yellow)",
  "bot.emergency_stop": "var(--mq-red)",
  "bot.mode": "var(--mq-cyan)",
  "bot.pair": "var(--mq-cyan)",
  "config.update": "var(--mq-cyan)",
  "sms.test": "var(--mq-text-1)",
};

export default function AuditLog() {
  const { t } = useI18n();
  const [rows, setRows] = useState([]);
  const [actionFilter, setActionFilter] = useState("");
  const [userFilter, setUserFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (actionFilter) params.action = actionFilter;
      if (userFilter) params.user_filter = userFilter;
      const { data } = await api.get("/audit", { params: { limit: 100, ...params } });
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn("audit load failed:", err?.message || err);
    } finally {
      setLoading(false);
    }
  }, [actionFilter, userFilter]);

  useEffect(() => { load(); }, [load]);

  // CSV download uses native browser navigation so the cookie + filename
  // disposition are honoured. We append filters as query params.
  const downloadCsv = () => {
    const qs = new URLSearchParams();
    if (actionFilter) qs.set("action", actionFilter);
    if (userFilter) qs.set("user_filter", userFilter);
    const url = `${BACKEND_URL}/api/audit/export.csv${qs.toString() ? `?${qs}` : ""}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="mq-panel p-4" data-testid="audit-log-panel">
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <h3 className="mq-heading text-sm tracking-widest uppercase">
          {t("auditTitle")}
        </h3>
        <div className="flex items-center gap-2">
          <input
            data-testid="audit-filter-action"
            value={actionFilter}
            onChange={(e) => setActionFilter(e.target.value)}
            placeholder={t("auditFilterAction")}
            className="mq-input text-xs"
            style={{ width: 140, padding: "0.25rem 0.5rem" }}
          />
          <input
            data-testid="audit-filter-user"
            value={userFilter}
            onChange={(e) => setUserFilter(e.target.value)}
            placeholder={t("auditFilterUser")}
            className="mq-input text-xs"
            style={{ width: 160, padding: "0.25rem 0.5rem" }}
          />
          <button
            data-testid="audit-refresh"
            className="mq-btn"
            onClick={load}
            disabled={loading}
            style={{ padding: "0.3rem 0.65rem" }}
          >
            ↻
          </button>
          <button
            data-testid="audit-export-csv"
            className="mq-btn mq-btn-primary"
            onClick={downloadCsv}
            style={{ padding: "0.3rem 0.75rem" }}
          >
            {t("auditExportCsv")}
          </button>
        </div>
      </div>

      <div className="mq-table-scroll mq-table-scroll--compact" data-testid="audit-log-scroll">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left mq-label">
              <th className="py-2 pr-3">{t("auditTimestamp")}</th>
              <th className="py-2 pr-3">{t("auditUser")}</th>
              <th className="py-2 pr-3">{t("auditAction")}</th>
              <th className="py-2 pr-3">{t("auditTarget")}</th>
              <th className="py-2 pr-3">{t("auditStatus")}</th>
              <th className="py-2 pr-3">{t("auditPayload")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={6} className="py-3 text-center text-[var(--mq-text-3)]">
                  {t("auditEmpty")}
                </td>
              </tr>
            )}
            {rows.map((r, i) => (
              <tr
                key={`${r.ts}-${i}`}
                data-testid="audit-row"
                className="border-t"
                style={{ borderColor: "var(--mq-border)" }}
              >
                <td className="py-2 pr-3 text-[var(--mq-text-2)] whitespace-nowrap">
                  {(r.ts_iso || "").replace("T", " ").slice(0, 19)}
                </td>
                <td className="py-2 pr-3 whitespace-nowrap">{r.user}</td>
                <td
                  className="py-2 pr-3 font-mono whitespace-nowrap"
                  style={{ color: ACTION_COLOR[r.action] || "var(--mq-text-1)" }}
                >
                  {r.action}
                </td>
                <td className="py-2 pr-3 text-[var(--mq-text-2)] whitespace-nowrap">
                  {r.target || "—"}
                </td>
                <td
                  className="py-2 pr-3 whitespace-nowrap"
                  style={{
                    color: r.status === "ok" ? "var(--mq-green)" : "var(--mq-red)",
                  }}
                >
                  {r.status}
                </td>
                <td className="py-2 pr-3 text-[var(--mq-text-3)] truncate max-w-md font-mono">
                  {r.payload && Object.keys(r.payload).length
                    ? JSON.stringify(r.payload)
                    : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
